import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Badge, Box, styled } from '@mui/material'
import { orange } from '@mui/material/colors'
import { PickersDay } from '@mui/x-date-pickers/PickersDay'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import { isSameWeek } from 'date-fns'
import { formatDateToBEFormatDateFns } from 'utils/date'
import { HEATMAP_VALUE_TO_RECURRENCE, RECURRENCE_TO_HEATMAP_VALUE } from 'settings/constants/workOrder'

// PickersDay replacement for the Map & Routing week selector — adds a heatmap tint,
// a top-right count badge, and a bottom-right unassigned indicator. Mirrors the
// Routing & Work Orders `EventsCalendar` visuals.
const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: prop => prop !== 'isSelected' && prop !== 'isHovered' && prop !== 'heatmapColor' && prop !== 'hasCount',
})(({ theme, isSelected, isHovered, heatmapColor, hasCount, day }) => ({
  borderRadius: 0,
  ...(hasCount && !isSelected && !isHovered && { borderRadius: '50%' }),
  ...(!isSelected && !isHovered && heatmapColor && { backgroundColor: heatmapColor }),
  ...(isSelected && {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.dark,
    },
  }),
  ...(isHovered && {
    backgroundColor: theme.palette.primary.light,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.light,
    },
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.primary.dark,
      '&:hover, &:focus': {
        backgroundColor: theme.palette.primary.dark,
      },
    }),
  }),
  ...(day.getDay() === 0 && {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
  }),
  ...(day.getDay() === 6 && {
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
  }),
}))

const isInSameWeek = (dayA, dayB) => {
  if (dayB == null) return false
  return isSameWeek(dayB, dayA)
}

const TOP_RIGHT_ANCHOR = { vertical: 'top', horizontal: 'right' }

const COUNT_BADGE_SX = theme => ({
  '& .MuiBadge-badge': {
    fontSize: 9,
    fontWeight: 700,
    height: 14,
    minWidth: 14,
    padding: '0 3px',
    lineHeight: 1,
    border: `2px solid ${theme.palette.background.paper}`,
  },
})

function PickersDayWithCount(props) {
  const { day, selectedDay, hoveredDay, countsByDate, ...other } = props

  const dayTime = day ? day.getTime() : null
  const dateKey = useMemo(() => (day ? formatDateToBEFormatDateFns(day) : null), [dayTime])

  const entry = dateKey && countsByDate ? countsByDate.get(dateKey) : undefined
  const total = entry?.total || 0
  const unassigned = entry?.unassigned || 0
  const heatmapValue = entry?.heatmapValue ?? RECURRENCE_TO_HEATMAP_VALUE.EMPTY
  const heatmapColor =
    total > 0 && heatmapValue !== RECURRENCE_TO_HEATMAP_VALUE.EMPTY ? HEATMAP_VALUE_TO_RECURRENCE[heatmapValue]?.color : undefined

  const dayCell = (
    <Box component="span" sx={{ position: 'relative', display: 'inline-flex' }}>
      <CustomPickersDay
        {...other}
        day={day}
        sx={{ px: 2.5 }}
        disableMargin
        selected={false}
        isSelected={isInSameWeek(day, selectedDay)}
        isHovered={isInSameWeek(day, hoveredDay)}
        heatmapColor={heatmapColor}
        hasCount={total > 0}
      />
      {unassigned > 0 && (
        <WarningRoundedIcon
          aria-label="has unassigned work orders"
          sx={{
            position: 'absolute',
            bottom: 0,
            right: -2,
            fontSize: 14,
            color: orange[600],
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      )}
    </Box>
  )

  if (total <= 0) return dayCell

  return (
    <Badge overlap="circular" color="primary" anchorOrigin={TOP_RIGHT_ANCHOR} badgeContent={total > 99 ? '99+' : total} sx={COUNT_BADGE_SX}>
      {dayCell}
    </Badge>
  )
}

PickersDayWithCount.propTypes = {
  day: PropTypes.object.isRequired,
  selectedDay: PropTypes.object,
  hoveredDay: PropTypes.object,
  countsByDate: PropTypes.instanceOf(Map),
}

export default PickersDayWithCount
